import * as React from 'react';
import get from 'get-value';
import { LocationAddressCardComponent } from 'components/location/cards/LocationAddressCardComponent';
import { LocationAddressEditCardComponent } from 'components/location/cards/LocationAddressEditCardComponent';
import { createLocationAddressForm } from 'components/location/forms/createLocationAddressForm';
import { Location } from 'components/location/models/LocationModel';
import {
    geoSuggestAddressFormatter,
    isValidAddress
} from 'components/utils/geoSuggest/geoSuggestAddressFormatter';
import { getGeoArrayFromObject } from 'components/utils/geoSuggest/getGeoArrayFromObject';
import { isValidCoordinate } from 'components/utils/isValidCoordinate';
import { MESSAGE_LOCATIONS_UPDATE_ERROR, MESSAGE_LOCATIONS_UPDATE_SUCCESS } from 'config/messages';
import { LegacyForm } from 'lib/LegacyForm';
import { Panel } from 'ui/Panel';
import { locationApi } from '../LocationApi';
import { useSnackbar } from 'notistack';

export interface LocationAddressCardProps {
    setCurrentLocation: (location: Location) => void;
    currentLocation: Location;
}

export const LocationAddressCard: React.FC<LocationAddressCardProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [geo, setGeo] = React.useState(currentLocation.geo);
    const { enqueueSnackbar } = useSnackbar();
    const form = React.useRef<LegacyForm>(null);
    const updateForm = React.useCallback(({ geo, address }: Location) => {
        const geoObject = Array.isArray(geo) ? { lat: geo[1], lng: geo[0] } : null;
        form.current?.update({ ...address, geo: geoObject });
    }, []);
    const getLocationUrl = React.useCallback(
        (lat: number, lng: number, placeId: string) =>
            `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&query_place_id=${placeId}`,
        []
    );
    const handleBlur = React.useCallback(
        (name: string, value: any) => {
            if (value && name === 'geo') {
                let googleMapsShortUrl: string;
                if (get(value, 'location')) {
                    const {
                        location: { lat, lng }
                    } = value;
                    const placeId = get(value, 'placeId');
                    if (isValidCoordinate([lng, lat])) {
                        setGeo([lng, lat]);
                        googleMapsShortUrl = getLocationUrl(lat, lng, placeId);
                    }
                }
                const address = geoSuggestAddressFormatter(value);
                if (isValidAddress(address)) {
                    form.current.update({ ...address, googleMapsShortUrl });
                }
            }
        },
        [getLocationUrl]
    );
    const handleSubmit = React.useCallback(
        async ({ geo, ...address }: any) => {
            const locationId = currentLocation._id;
            const response = await locationApi.update(locationId, {
                body: {
                    address,
                    geo: getGeoArrayFromObject(geo)
                }
            });
            if (response.ok) {
                enqueueSnackbar(MESSAGE_LOCATIONS_UPDATE_SUCCESS(currentLocation.title), {
                    variant: 'success'
                });
                updateForm(response.body);
                setCurrentLocation(response.body);
                setGeo(getGeoArrayFromObject(geo));
            } else {
                enqueueSnackbar(MESSAGE_LOCATIONS_UPDATE_ERROR, { variant: 'error' });
            }
            setIsEditing(false);
        },
        [currentLocation._id, currentLocation.title, enqueueSnackbar, setCurrentLocation, updateForm]
    );
    React.useEffect(() => {
        form.current = new LegacyForm(createLocationAddressForm(), handleSubmit);
    }, [handleSubmit]);
    const toggleState = React.useCallback(() => {
        setIsEditing(currentValue => !currentValue);
        setGeo(currentLocation.geo);
        updateForm(currentLocation);
    }, [currentLocation, updateForm]);
    return (
        <Panel title="Address" showAction={!isEditing} onClick={toggleState} divider>
            {isEditing ? (
                <LocationAddressEditCardComponent
                    onBlur={handleBlur}
                    onClose={toggleState}
                    form={form.current}
                    geo={geo}
                />
            ) : (
                <LocationAddressCardComponent location={currentLocation} />
            )}
        </Panel>
    );
};
